import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 anywhere in admin land, clear the stale token and bounce to login
// (prevents the brief "Failed to load …" toast on every admin page).
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && typeof window !== 'undefined') {
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      const onLogin = window.location.pathname === '/admin/login';
      if (isAdminRoute && !onLogin) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.replace('/admin/login');
      }
    }
    return Promise.reject(error);
  }
);

// Public API calls
export const subscribeNewsletter = (email) => api.post(`/newsletter/subscribe?email=${encodeURIComponent(email)}`);
export const submitContact = (data) => api.post('/contact', data);

// File Upload
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getFileUrl = (fileId) => `${API}/files/${fileId}`;

// Spotlight Events
export const submitSpotlight = (data) => api.post('/spotlight/submit', data);
export const createSpotlightCheckout = (spotlightId, originUrl) => 
  api.post(`/spotlight/checkout?spotlight_id=${spotlightId}&origin_url=${encodeURIComponent(originUrl)}`);
export const getSpotlightStatus = (sessionId) => api.get(`/spotlight/status/${sessionId}`);
export const getActiveSpotlights = () => api.get('/spotlight/active');
export const getAdminSpotlights = () => api.get('/admin/spotlights');
export const deleteSpotlight = (id) => api.delete(`/admin/spotlights/${id}`);
export const toggleSpotlight = (id) => api.put(`/admin/spotlights/${id}/toggle`);

export const getExternalFeed = () => api.get('/external-feed');

// Jobs & Directory (public + by-slug for detail pages)
export const getJobs = () => api.get('/jobs');
export const getJobBySlug = (slug) => api.get(`/jobs/by-slug/${slug}`);
export const getDirectory = (params) => api.get('/directory', { params });
export const getDirectoryBySlug = (slug) => api.get(`/directory/by-slug/${slug}`);

// Admin Auth
export const adminLogin = (data) => api.post('/admin/login', data);
export const adminRegister = (data) => api.post('/admin/register', data);
export const getAdminProfile = () => api.get('/admin/me');

// Admin API calls
export const getAdminStats = () => api.get('/admin/stats');

export const getNewsletterSubscribers = () => api.get('/admin/newsletter');
export const deleteNewsletterSubscriber = (id) => api.delete(`/admin/newsletter/${id}`);
export const sendNewsletterCampaign = (data) => api.post('/admin/newsletter/send', data);
export const getNewsletterCampaigns = () => api.get('/admin/newsletter/campaigns');
export const getContactMessages = () => api.get('/admin/contacts');
export const markContactRead = (id) => api.put(`/admin/contacts/${id}/read`);
export const deleteContact = (id) => api.delete(`/admin/contacts/${id}`);

// Admin Analytics
export const getAdminAnalytics = () => api.get('/admin/analytics/overview');

// Admin profile
export const changeAdminPassword = (data) => api.post('/admin/change-password', data);

// Admin user management (super_admin only)
export const listAdmins = () => api.get('/admin/admins');
export const createAdminUser = (data) => api.post('/admin/register', data);
export const updateAdminUser = (id, data) => api.put(`/admin/admins/${id}`, data);
export const deleteAdminUser = (id) => api.delete(`/admin/admins/${id}`);

// Directory CSV import
export const importDirectoryCSV = (formData) =>
  api.post('/admin/directory/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Jobs CSV import
export const importJobsCSV = (formData) =>
  api.post('/admin/jobs/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Jobs review queue (ClydeFlow + manual drafts)
export const getJobDrafts = () => api.get('/admin/jobs/drafts');
export const bulkPublishJobs = (ids) => api.post('/admin/jobs/bulk-publish', { ids });
export const bulkDeleteJobs = (ids) => api.post('/admin/jobs/bulk-delete', { ids });

// Featured Job (paid placement)
export const submitFeaturedJob = (data) => api.post('/featured-job/submit', data);
export const createFeaturedJobCheckout = (featuredId, originUrl) =>
  api.post(`/featured-job/checkout?featured_id=${featuredId}&origin_url=${encodeURIComponent(originUrl)}`);
export const getFeaturedJobStatus = (sessionId) => api.get(`/featured-job/status/${sessionId}`);
export const getActiveFeaturedJobs = () => api.get('/featured-job/active');
export const getAdminFeaturedJobs = () => api.get('/admin/featured-jobs');
export const toggleFeaturedJob = (id) => api.put(`/admin/featured-jobs/${id}/toggle`);
export const deleteFeaturedJob = (id) => api.delete(`/admin/featured-jobs/${id}`);

// Post a Job (self-serve paid listing)
export const submitPaidJob = (data) => api.post('/paid-job/submit', data);
export const createPaidJobCheckout = (paidId, originUrl) =>
  api.post(`/paid-job/checkout?paid_id=${paidId}&origin_url=${encodeURIComponent(originUrl)}`);
export const getPaidJobStatus = (sessionId) => api.get(`/paid-job/status/${sessionId}`);
export const getAdminPaidJobs = () => api.get('/admin/paid-jobs');
export const deletePaidJob = (id) => api.delete(`/admin/paid-jobs/${id}`);

// Social Posts (LinkedIn / Facebook / X auto-post log)
export const getAdminSocialPosts = (limit = 50) => api.get(`/admin/social-posts?limit=${limit}`);
export const testAdminSocialPost = () => api.post('/admin/social-posts/test');

// LinkedIn OAuth Connect
export const getLinkedInStatus = () => api.get('/admin/social/linkedin/status');
export const saveLinkedInApp = (client_id, client_secret) =>
  api.post('/admin/social/linkedin/save-app', { client_id, client_secret });
export const getLinkedInAuthorizeUrl = (scope = 'default') =>
  api.get(`/admin/social/linkedin/authorize-url?scope=${scope}&origin=${encodeURIComponent(window.location.origin)}`);
export const disconnectLinkedIn = () => api.post('/admin/social/linkedin/disconnect');

// X (Twitter) OAuth Connect
export const getXStatus = () => api.get('/admin/social/x/status');
export const saveXApp = (client_id, client_secret) =>
  api.post('/admin/social/x/save-app', { client_id, client_secret });
export const getXAuthorizeUrl = () =>
  api.get(`/admin/social/x/authorize-url?origin=${encodeURIComponent(window.location.origin)}`);
export const disconnectX = () => api.post('/admin/social/x/disconnect');

// Admin Directory CRUD
export const getAdminDirectory = () => api.get('/admin/directory');
export const createDirectoryBusiness = (data) => api.post('/admin/directory', data);
export const updateDirectoryBusiness = (id, data) => api.put(`/admin/directory/${id}`, data);
export const deleteDirectoryBusiness = (id) => api.delete(`/admin/directory/${id}`);
export const purgeDirectorySeeds = () =>
  api.post('/admin/directory/purge-seeds', {}, { timeout: 60000 });
export const scrapeDirectoryOSM = (dryRun = false) =>
  api.post(`/admin/directory/scrape?dry_run=${dryRun}`, {}, { timeout: 180000 });

// News / Blog (public)
export const getNews = () => api.get('/news');
export const getNewsBySlug = (slug) => api.get(`/news/by-slug/${slug}`);

// News / Blog (admin)
export const getAdminNews = () => api.get('/admin/news');
export const createNewsPost = (data) => api.post('/admin/news', data);
export const updateNewsPost = (id, data) => api.put(`/admin/news/${id}`, data);
export const deleteNewsPost = (id) => api.delete(`/admin/news/${id}`);
export const draftNewsWithAI = (data) => api.post('/admin/news/draft-ai', data);
export const weeklySuggest = () => api.get('/admin/news/weekly-suggest');
export const generateWeeklyPost = (data = {}) => api.post('/admin/news/weekly-generate', data);
export const blastNewsPost = (id, data = {}) => api.post(`/admin/news/${id}/blast`, data);

// Media Library
export const listMedia = () => api.get('/admin/media');
export const uploadMedia = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/media/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteMedia = (id) => api.delete(`/admin/media/${id}`);

// ClydeFlow Import
export const clydeflowPreview = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/clydeflow/preview', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
};
export const clydeflowCommit = (session_id, selected_indices) =>
  api.post('/admin/clydeflow/commit', { session_id, selected_indices }, { timeout: 300000 });
export const clydeflowHistory = () => api.get('/admin/clydeflow/history');
export const clydeflowDriveStatus = () => api.get('/admin/clydeflow/drive-status');
export const clydeflowDriveScanNow = () => api.post('/admin/clydeflow/drive-scan-now', {}, { timeout: 300000 });
export const clydeflowDriveConnect = () => api.get('/admin/clydeflow/drive-connect');
export const clydeflowDriveDisconnect = () => api.delete('/admin/clydeflow/drive-disconnect');
export const clydeflowRetentionStats = () => api.get('/admin/clydeflow/retention-stats');
export const clydeflowRetentionRunNow = () => api.post('/admin/clydeflow/retention-run-now');
export const clydeflowConnectServiceAccount = (json_key, folder_id) =>
  api.post('/admin/clydeflow/drive-connect-service-account', { json_key, folder_id }, { timeout: 60000 });

export default api;
